import React, { useEffect, useRef, useState } from 'react'
import { executeCapability } from '../utils/capabilityExecute'

export type EmployeeSuggestion = {
  employeeId: string
  name: string
  preferredName?: string | null
  position?: string | null
}

function displayName(emp: EmployeeSuggestion) {
  const preferred = String(emp.preferredName || '').trim()
  const name = String(emp.name || '').trim()
  return preferred || name || emp.employeeId
}

export function EmployeeTypeahead({
  selected,
  onSelect,
  onClear,
  placeholder = 'Search employees…',
  app = 'forms',
  capability = 'forms.searchEmployees',
}: {
  selected?: { employeeId: string; label: string } | null
  onSelect: (emp: EmployeeSuggestion) => void
  onClear?: () => void
  placeholder?: string
  app?: string
  capability?: string
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<EmployeeSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [])

  const search = (term: string) => {
    setQuery(term)
    if (timerRef.current) window.clearTimeout(timerRef.current)
    if (term.trim().length < 2) {
      setResults([])
      return
    }
    timerRef.current = window.setTimeout(async () => {
      setLoading(true)
      try {
        const data = await executeCapability<EmployeeSuggestion[]>({
          app,
          capability,
          input: { q: term.trim() },
        })
        setResults(Array.isArray(data) ? data : [])
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 250) as unknown as number
  }

  if (selected?.employeeId) {
    return (
      <div className="flex items-center gap-2">
        <span className="badge badge-outline gap-1">
          {selected.label || selected.employeeId}
        </span>
        {onClear && (
          <button type="button" className="btn btn-ghost btn-xs" onClick={onClear}>
            Change
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <input
        className="input input-bordered input-sm w-full"
        value={query}
        onChange={(e) => search(e.target.value)}
        placeholder={placeholder}
      />
      {loading && <div className="text-xs text-base-content/50 mt-1">Searching…</div>}
      {results.length > 0 && (
        <ul className="menu bg-base-100 rounded-box border mt-1 w-full z-10 absolute shadow">
          {results.map((emp) => (
            <li key={emp.employeeId}>
              <a
                onClick={() => {
                  onSelect(emp)
                  setQuery('')
                  setResults([])
                }}
              >
                <span>{displayName(emp)}</span>
                <span className="text-xs text-base-content/50">
                  {emp.employeeId}{emp.position ? ` · ${emp.position}` : ''}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
